// What a dig or a check reads: the sessions of one case, read the way the
// Triage tab's "Investigate in chat" reads one (readCitedRecord: the whole
// record when it fits, slices around each citation when it does not), the
// endpoint's own records of what ran during each session (processes, network
// connections, DNS lookups, file events, joined to the session by the
// endpoint), and the other sessions on the same machine around then.
//
// Everything the tools hand the model is also kept here, normalized, as the
// corpus a quote has to come from: a correction or a refuted claim stands
// only on words the model was actually shown, as the backend verifier's
// harness holds its witnesses to the ref they cite.
import { Type } from "@earendil-works/pi-ai";
import { readConversationPage, citationKeysets, cursorAfter } from "./conversationRead.js";
import { rangeToTimeRange, runQueryRows } from "./analyticsClient.js";
import { LITE_READ_LEAD_MS, normalizeSpan, readCitedRecord, renderWindow } from "./triageReadKit.js";
import { turnText } from "./transcriptText.js";
import { sessionOutline } from "./sessionOutline.js";
import { buildTraceLanes, markKindOf } from "./traceLanes.js";
import { parseServerTimestamp } from "./traceUtils.js";

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();
const short = (id) => str(id).slice(0, 8);
const clip = (s, n) => (s.length > n ? `${s.slice(0, n)} ...` : s);
const text = (body, details = undefined) => ({ content: [{ type: "text", text: body }], ...(details ? { details } : {}) });
const toolError = (message) => ({ content: [{ type: "text", text: message }], isError: true });

export const READ_RADIUS = 4;
export const READ_RADIUS_MAX = 12;
const CALL_CHARS = 4000;
const SEARCH_HITS = 12;
const LIST_CALLS_MAX = 60;
export const RECORD_ROWS = 120;
const RECORD_LINE = 320;
// Files the sessions share that a walkthrough is told of, at most.
export const SHARED_FILES_MAX = 12;
// A file as sessions share it: its folder and name, which a clone elsewhere
// keeps the same (as the workspace joins its traces' files).
const fileKey = (path) => str(path).replace(/\\/g, "/").split("/").filter(Boolean).slice(-2).join("/").toLowerCase();
// A quote shorter than this proves nothing: too many places hold it.
export const QUOTE_MIN = 12;
// The tool names a dig and a check read other sessions' records with. The
// trace review backend (skwach attribution.py RECORD_TOOLS) recognizes a
// session calling them as one reviewing other sessions.
export const CASE_TOOL_NAMES = ["investigate_read_turns", "investigate_list_calls", "investigate_read_call", "investigate_search", "investigate_machine_records", "investigate_nearby_sessions"];

// The machine's own records of a session, by kind: the entity, the fields a
// row is drawn from, and the one a `contains` filter matches.
const RECORD_KINDS = {
    process: {
        entity: "ENTITY_TYPE_PROCESS_EVENT", prefix: "PROCESS_EVENT", match: "COMMAND_LINE",
        fields: ["EVENT_TIME", "IMAGE_NAME", "COMMAND_LINE", "ACCOUNT_NAME", "PROCESS_ID"],
        line: (r) => `${r.image_name || "?"}${r.account_name ? ` (${r.account_name})` : ""}: ${r.command_line || "(no command line)"}`,
    },
    network: {
        entity: "ENTITY_TYPE_NETWORK_CONNECTION", prefix: "NETWORK_CONNECTION", match: "REMOTE_ADDRESS",
        fields: ["EVENT_TIME", "REMOTE_ADDRESS", "REMOTE_PORT", "BYTES_SENT", "BYTES_RECEIVED", "DURATION_MS"],
        line: (r) => `to ${r.remote_address || "?"}:${r.remote_port ?? "?"} sent ${r.bytes_sent ?? "?"} B, received ${r.bytes_received ?? "?"} B${r.duration_ms != null ? `, ${r.duration_ms} ms` : ""}`,
    },
    dns: {
        entity: "ENTITY_TYPE_DNS_EVENT", prefix: "DNS_EVENT", match: "QUERY_NAME",
        fields: ["EVENT_TIME", "QUERY_NAME", "RECORD_TYPE", "RESPONSE_CODE", "RESOLVED_ADDRESSES"],
        line: (r) => `looked up ${r.query_name || "?"} (${r.record_type || "?"}) -> ${Array.isArray(r.resolved_addresses) ? r.resolved_addresses.slice(0, 4).join(", ") : str(r.resolved_addresses) || r.response_code || "no answer"}`,
    },
    file: {
        entity: "ENTITY_TYPE_FILE_EVENT", prefix: "FILE_EVENT", match: "PRIMARY_PATH",
        fields: ["EVENT_TIME", "KIND", "ACTIONS", "PRIMARY_PATH", "DESTINATION_PATH", "SIZE"],
        line: (r) => `${r.kind || "file"} ${Array.isArray(r.actions) ? r.actions.join("/") : str(r.actions)} ${r.primary_path || "?"}${r.destination_path ? ` -> ${r.destination_path}` : ""}${r.size != null ? ` (${r.size} B)` : ""}`,
    },
};
export const RECORD_KIND_NAMES = Object.keys(RECORD_KINDS);
const fieldOf = (kind, name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_${RECORD_KINDS[kind].prefix}_${name}` });
const likeTerm = (t) => `%${str(t).replace(/[\\%_]/g, (c) => `\\${c}`)}%`;

// A record read of one session: its rows in time order, at most `limit`.
export function recordSpec({ kind, session, timeRange, contains = "", limit = RECORD_ROWS }) {
    const k = RECORD_KINDS[kind];
    const traceFilter = { pred: { field: fieldOf(kind, "TRACE_ID"), op: "OPERATOR_EQ", value: [lower(session)] } };
    const filter = contains
        ? { andExpr: { children: [traceFilter, { pred: { field: fieldOf(kind, k.match), op: "OPERATOR_ILIKE", value: [likeTerm(contains)] } }] } }
        : traceFilter;
    return {
        root: { alias: "e", entity: k.entity },
        timeRange,
        filter,
        output: { dimensions: k.fields.map((f) => ({ field: fieldOf(kind, f), outputAlias: f.toLowerCase() })) },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }],
        limit,
    };
}

// The sessions an endpoint ran around a moment: each once, with when it ran
// and what started it.
const T = (name) => ({ stepAlias: "t", field: `DIMENSION_FIELD_CONVERSATION_TRACE_${name}` });
export function nearbySpec({ endpoint, fromMs, toMs, limit = 60 }) {
    return {
        root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" },
        timeRange: rangeToTimeRange({ from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() }),
        filter: { pred: { field: T("ENDPOINT_ID"), op: "OPERATOR_EQ", value: [lower(endpoint)] } },
        output: {
            dimensions: [
                { field: T("TRACE_ID"), outputAlias: "trace_id" },
                { field: T("PARENT_TRACE_ID"), outputAlias: "parent_trace_id" },
                { field: T("PROVIDER"), outputAlias: "provider" },
                { field: T("CLIENT_SURFACE"), outputAlias: "surface" },
            ],
            metrics: [
                { outputAlias: "first_at", computed: { field: T("EVENT_TIME"), op: "AGGREGATE_OP_MIN" } },
                { outputAlias: "last_at", computed: { field: T("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } },
            ],
        },
        orderBy: [{ outputAlias: "first_at", direction: "DIRECTION_ASC" }],
        limit,
    };
}

const timeOf = (v) => {
    const ms = parseServerTimestamp(v);
    return Number.isFinite(ms) ? new Date(ms).toISOString().replace("T", " ").slice(0, 19) : "?";
};

// The corpus: every text the tools returned, normalized, and every message
// id a rendered window showed, per session.
export function createCorpus() {
    // Each body shown, with where it came from: a session's record, or the
    // machine's own records ("machine").
    const seen = [];
    const messages = new Map();
    return {
        add(body, from = "session") { const t = normalizeSpan(body); if (t) seen.push({ t, from }); },
        addMessages(session, ids) {
            const s = lower(session);
            if (!messages.has(s)) messages.set(s, new Set());
            for (const id of ids) if (id) messages.get(s).add(lower(id));
        },
        // Whether `quote` was shown, word for word once normalized.
        holds(quote) {
            const q = normalizeSpan(quote);
            return q.length >= QUOTE_MIN && seen.some((x) => x.t.includes(q));
        },
        // Where a held quote was shown: "machine" when only the machine's own
        // records showed it, else "session"; null when nothing showed it.
        sourceOf(quote) {
            const q = normalizeSpan(quote);
            if (q.length < QUOTE_MIN) return null;
            const hits = seen.filter((x) => x.t.includes(q));
            if (!hits.length) return null;
            return hits.every((x) => x.from === "machine") ? "machine" : "session";
        },
        // Whether a message of `session` was shown by its id.
        showed(session, messageId) { return Boolean(messageId) && Boolean(messages.get(lower(session))?.has(lower(messageId))); },
        // Every message shown, by session ({ [session]: [ids] }), for a
        // reader carrying on this one's conversation to start from.
        shownMessages() { return Object.fromEntries([...messages].map(([s, ids]) => [s, [...ids]])); },
        get size() { return seen.length; },
    };
}

// `sessions`: [{ id, ref, endpoint?, findings: [{ id, ref, messages, quotes, completedAt }], messages? }],
// `messages` other messages of the session its record is read around.
// `window`: the lens's review window ({ fromMs, toMs }), which the record
// read opens well before. Returns { tools, corpus, stats() }.
// `onRead({ session, messageId })` hears where each read lands: the message a
// turn read centres on, a tool call read, or a search's first hit.
// A reader a conversation keeps across its runs hands in its own `corpus`
// and `records` (by session), so what it was shown and read before still
// counts; `describe(id)` resolves a session it knows nothing else of to {
// window, endpoint } (the span it ran over and its machine), which its reads
// use in place of `window` and its `endpoint`; `what` names the sessions in
// what the tools say ("this case").
export function createCaseReader({
    sessions = [], window = null, nowMs = Date.now(),
    readPage = readConversationPage, keysets = citationKeysets, anchorAfter = cursorAfter, query = runQueryRows, onRead = null,
    corpus = createCorpus(), records = new Map(), describe = null, what = "this case",
} = {}) {
    const list = sessions.filter((s) => s?.id).map((s, i) => ({ ...s, id: lower(s.id), ref: s.ref || `S${i + 1}` }));
    const byRef = new Map();
    for (const s of list) { byRef.set(s.ref.toLowerCase(), s); byRef.set(s.id, s); byRef.set(short(s.id), s); }
    let reads = 0;
    const fromMs = Number.isFinite(window?.fromMs) ? window.fromMs : nowMs - 30 * 86400000;
    const toMs = Number.isFinite(window?.toMs) ? window.toMs : nowMs;
    const rangeOf = (from, to) => rangeToTimeRange({ from: new Date(from - LITE_READ_LEAD_MS).toISOString(), to: new Date(Math.max(to, from) + 3600000).toISOString() });
    const timeRange = rangeOf(fromMs, toMs);
    // What a session is, as `describe` has it: once each, and nothing where it
    // cannot say.
    const described = new Map();
    const factsOf = (s) => {
        if (!describe) return Promise.resolve(null);
        if (!described.has(s.id)) described.set(s.id, Promise.resolve().then(() => describe(s.id)).catch(() => null));
        return described.get(s.id);
    };
    const rangeFor = async (s) => {
        const w = (await factsOf(s))?.window;
        return Number.isFinite(w?.fromMs) && Number.isFinite(w?.toMs) ? rangeOf(w.fromMs, w.toMs) : timeRange;
    };
    const endpointOf = async (s) => s.endpoint || (await factsOf(s))?.endpoint || null;

    const sessionOf = (raw) => byRef.get(lower(raw)) || null;
    const unknown = (raw) => toolError(`No session "${str(raw).slice(0, 40)}" in ${what}. Use ${list.map((s) => s.ref).join(", ")}.`);
    const loadRecord = (s) => {
        if (!records.has(s.id)) {
            records.set(s.id, rangeFor(s).then((range) => readCitedRecord({
                traceId: s.id, timeRange: range, readPage, keysets, anchorAfter,
                messageIds: [...(s.findings || []).flatMap((f) => f.messages || []), ...(s.messages || [])],
            })).catch((error) => { records.delete(s.id); throw error; }));
        }
        return records.get(s.id);
    };
    const shown = (s, body, turns = []) => {
        reads += 1;
        corpus.add(body);
        corpus.addMessages(s.id, turns.map((t) => t?.messageId));
        return body;
    };
    const heard = (s, turn) => {
        if (!onRead || !turn) return;
        try { onRead({ session: s.id, messageId: turn.messageId || null }); } catch { /* the listener's failure is its own */ }
    };
    const bodies = new WeakMap();
    const matchBodies = (turns) => {
        let b = bodies.get(turns);
        if (!b) { b = turns.map((t) => normalizeSpan(turnText(t))); bodies.set(turns, b); }
        return b;
    };
    // Where a finding's citation sits in what was read: its cited message,
    // else the first of its quoted words found.
    const locate = (turns, finding) => {
        if (!finding) return -1;
        const wanted = new Set((finding.messages || []).map(lower));
        const i = turns.findIndex((t) => wanted.has(lower(t.messageId)));
        if (i >= 0) return i;
        const b = matchBodies(turns);
        for (const q of (finding.quotes || []).map(normalizeSpan)) {
            for (const n of [q.slice(0, 60), q.slice(0, 30)]) {
                if (n.length < QUOTE_MIN) continue;
                const at = b.findIndex((x) => x.includes(n));
                if (at >= 0) return at;
            }
        }
        return -1;
    };
    const readNote = (r) => (r.complete ? "" : ` (read ${r.turns.filter((t) => t.role !== "gap").length} of ${r.total} messages: the parts around the citations and the newest; a search that finds nothing covers only these)`);

    const readTurns = {
        name: "investigate_read_turns",
        label: "Read a session",
        description: `A case session's turns around a point, with tool calls and their results. Centre on a finding's citation (finding: F1), a message (its id, as [brackets] show it), a quote, or a turn number; with none, on the session's first finding. Radius default ${READ_RADIUS}, max ${READ_RADIUS_MAX}. Turn numbers count within what was read.`,
        parameters: Type.Object({
            session: Type.String({ description: "S1, S2... or a session id" }),
            finding: Type.Optional(Type.String({ description: "a finding ref (F1) to centre on its citation" })),
            message: Type.Optional(Type.String({ description: "a message id, as [brackets] show it, to centre on" })),
            around: Type.Optional(Type.String({ description: "a quote to centre on" })),
            turn: Type.Optional(Type.Number({ description: "a turn number to centre on" })),
            radius: Type.Optional(Type.Number()),
        }),
        async execute(_id, args) {
            const s = sessionOf(args.session);
            if (!s) return unknown(args.session);
            let r;
            try { r = await loadRecord(s); } catch (error) { return toolError(`Could not read ${s.ref}: ${error?.message || error}`); }
            const turns = r.turns;
            if (!turns.length) return text(`${s.ref} has no readable messages in this window.`);
            let at = -1;
            if (Number.isFinite(args.turn) && args.turn > 0) at = Math.min(turns.length - 1, Math.floor(args.turn) - 1);
            else if (args.message) {
                const want = lower(args.message).replace(/^\[|\]$/g, "");
                at = turns.findIndex((t) => lower(t.messageId) === want);
                if (at < 0) return text(`Message [${clip(want, 60)}] is not among ${s.ref}'s ${turns.length} messages read.${readNote(r)}`);
            } else if (args.around) {
                const needle = normalizeSpan(args.around).slice(0, 60);
                at = needle.length >= 3 ? matchBodies(turns).findIndex((b) => b.includes(needle)) : -1;
                if (at < 0) return text(`Nothing in ${s.ref}'s ${turns.length} messages read matches that quote.${readNote(r)}`);
            } else {
                const f = args.finding ? (s.findings || []).find((x) => lower(x.ref) === lower(args.finding)) : (s.findings || [])[0];
                at = locate(turns, f);
                if (at < 0) return text(`${args.finding || "The finding"}'s citation is not among ${s.ref}'s ${turns.length} messages read.${readNote(r)}`);
            }
            const radius = Math.max(1, Math.min(READ_RADIUS_MAX, Number(args.radius) || READ_RADIUS));
            const window = turns.slice(Math.max(0, at - radius), at + radius + 1);
            heard(s, turns[at]);
            return text(shown(s, `${s.ref} ${renderWindow(turns, at, radius, s.id)}${readNote(r)}`, window));
        },
    };

    const listCalls = {
        name: "investigate_list_calls",
        label: "List a session's tool calls",
        description: `Every tool call in a case session, in order: turn, tool, and the start of its arguments. The way to find an act when a citation does not land on it. Up to ${LIST_CALLS_MAX} a page.`,
        parameters: Type.Object({
            session: Type.String({ description: "S1, S2... or a session id" }),
            name: Type.Optional(Type.String({ description: "only calls of this tool" })),
            offset: Type.Optional(Type.Number()),
        }),
        async execute(_id, args) {
            const s = sessionOf(args.session);
            if (!s) return unknown(args.session);
            let r;
            try { r = await loadRecord(s); } catch (error) { return toolError(`Could not read ${s.ref}: ${error?.message || error}`); }
            const want = lower(args.name);
            const rows = [];
            r.turns.forEach((t, i) => {
                for (const b of t.blocks || []) {
                    if (b.type !== "tool_use" || (want && lower(b.name) !== want)) continue;
                    rows.push(`T${i + 1} ${b.name || "?"}: ${b.input == null ? "(arguments not captured)" : clip(JSON.stringify(b.input), 180)}`);
                }
            });
            if (!rows.length) return text(`${s.ref} has no ${want ? `${want} ` : ""}tool calls in what was read.${readNote(r)}`);
            const offset = Math.max(0, Math.floor(Number(args.offset) || 0));
            const page = rows.slice(offset, offset + LIST_CALLS_MAX);
            return text(shown(s, [`${s.ref}: ${rows.length} tool call(s); showing ${offset + 1}-${offset + page.length}${readNote(r)}`, ...page].join("\n")));
        },
    };

    const readCall = {
        name: "investigate_read_call",
        label: "Read a tool call",
        description: `One tool call of a case session in full: its arguments or its result, up to ${CALL_CHARS} characters. Use it whenever the act in question is something the agent ran.`,
        parameters: Type.Object({
            session: Type.String({ description: "S1, S2... or a session id" }),
            turn: Type.Number({ description: "the turn the call is on" }),
            index: Type.Optional(Type.Number({ description: "which tool block on that turn, 1-based" })),
        }),
        async execute(_id, args) {
            const s = sessionOf(args.session);
            if (!s) return unknown(args.session);
            let r;
            try { r = await loadRecord(s); } catch (error) { return toolError(`Could not read ${s.ref}: ${error?.message || error}`); }
            const i = Math.floor(Number(args.turn)) - 1;
            if (!(i >= 0 && i < r.turns.length)) return toolError(`No turn ${args.turn} in ${s.ref}'s ${r.turns.length} messages read.`);
            const blocks = (r.turns[i].blocks || []).filter((b) => b.type === "tool_use" || b.type === "tool_result");
            if (!blocks.length) return text(`T${i + 1} of ${s.ref} carries no tool call.`);
            const pick = Math.max(1, Math.floor(Number(args.index) || 1));
            const b = blocks[pick - 1];
            if (!b) return text(`T${i + 1} of ${s.ref} carries ${blocks.length} tool blocks; ${pick} is past the end.`);
            const lines = [`${s.ref} T${i + 1}${r.turns[i].messageId ? ` [${r.turns[i].messageId}]` : ""} - tool block ${pick} of ${blocks.length}`];
            if (b.type === "tool_use") {
                lines.push(`tool: ${b.name || "?"}`);
                lines.push(b.input == null ? "arguments: not captured (this says nothing about whether the act happened)" : `arguments: ${clip(JSON.stringify(b.input, null, 1), CALL_CHARS)}`);
            } else {
                const body = typeof b.content === "string" ? b.content : JSON.stringify(b.content ?? null, null, 1);
                lines.push(`result of: ${b.tool_name || "?"}${b.isError ? " (error)" : ""}`);
                lines.push(body ? `result: ${clip(body, CALL_CHARS)}` : "result: not captured");
            }
            heard(s, r.turns[i]);
            return text(shown(s, lines.join("\n"), [r.turns[i]]));
        },
    };

    const search = {
        name: "investigate_search",
        label: "Search the case's sessions",
        description: `Find text in one case session, or in all of them, tool calls and results included. Up to ${SEARCH_HITS} hits a session.`,
        parameters: Type.Object({
            query: Type.String({ description: "the text to find (at least 3 characters)" }),
            session: Type.Optional(Type.String({ description: "S1, S2... or a session id; every case session when left out" })),
        }),
        async execute(_id, args) {
            const needle = normalizeSpan(args.query);
            if (needle.length < 3) return toolError("Give at least three characters to search for.");
            const targets = args.session ? [sessionOf(args.session)] : list;
            if (targets.some((s) => !s)) return unknown(args.session);
            const out = [];
            for (const s of targets) {
                let r;
                try { r = await loadRecord(s); } catch (error) { out.push(`${s.ref}: could not read (${error?.message || error})`); continue; }
                const b = matchBodies(r.turns);
                const hits = [];
                const hitTurns = [];
                for (let i = 0; i < r.turns.length && hits.length < SEARCH_HITS; i++) {
                    const at = b[i].indexOf(needle);
                    if (at < 0) continue;
                    const raw = turnText(r.turns[i]).replace(/\s+/g, " ").trim();
                    const from = Math.max(0, Math.round((at / Math.max(1, b[i].length)) * raw.length) - 80);
                    if (!hits.length) heard(s, r.turns[i]);
                    hits.push(`  T${i + 1} ${r.turns[i].role}${r.turns[i].messageId ? ` [${r.turns[i].messageId}]` : ""}: ...${clip(raw.slice(from), 260)}`);
                    hitTurns.push(r.turns[i]);
                }
                out.push(hits.length ? `${s.ref}: ${hits.length} match(es)\n${hits.join("\n")}` : `${s.ref}: no match in ${r.turns.length} messages read${readNote(r)}`);
                corpus.addMessages(s.id, hitTurns.map((t) => t.messageId));
            }
            reads += 1;
            const body = out.join("\n");
            corpus.add(body);
            return text(body);
        },
    };

    const machineRecords = {
        name: "investigate_machine_records",
        label: "Read what ran on the machine",
        description: `The endpoint's own records of what happened during a case session, in time order: processes it started (command lines), network connections (where to, bytes sent), DNS lookups, or file events. Independent of what the agent said it did. Up to ${RECORD_ROWS} rows; \`contains\` narrows to rows whose command line, address, name or path holds the text.`,
        parameters: Type.Object({
            session: Type.String({ description: "S1, S2... or a session id" }),
            kind: Type.Union(RECORD_KIND_NAMES.map((k) => Type.Literal(k))),
            contains: Type.Optional(Type.String()),
        }),
        async execute(_id, args) {
            const s = sessionOf(args.session);
            if (!s) return unknown(args.session);
            const kind = RECORD_KINDS[args.kind] ? args.kind : null;
            if (!kind) return toolError(`kind must be one of ${RECORD_KIND_NAMES.join(", ")}.`);
            const contains = str(args.contains).trim();
            let rows;
            try {
                rows = await query(recordSpec({ kind, session: s.id, timeRange: await rangeFor(s), contains }));
            } catch (error) {
                return toolError(`Could not read the ${kind} records of ${s.ref}: ${error?.serverMessage || error?.message || error}`);
            }
            if (!rows?.length) {
                const body = `${s.ref}: no ${kind} records${contains ? ` holding "${contains.slice(0, 60)}"` : ""} attributed to this session. The endpoint may not have recorded this kind for it; absence here is not proof the act did not happen.`;
                corpus.add(body, "machine");
                reads += 1;
                return text(body);
            }
            const lines = rows.map((r) => `  ${timeOf(r.event_time)} ${clip(RECORD_KINDS[kind].line(r).replace(/\s+/g, " "), RECORD_LINE)}`);
            const body = [`${s.ref}: ${rows.length}${rows.length >= RECORD_ROWS ? "+" : ""} ${kind} record(s)${contains ? ` holding "${contains.slice(0, 60)}"` : ""}`, ...lines].join("\n");
            reads += 1;
            corpus.add(body, "machine");
            return text(body);
        },
    };

    const nearby = {
        name: "investigate_nearby_sessions",
        label: "Find sessions on the same machine",
        description: "Other AI sessions on the machine a case session ran on, from 6 hours before its findings to 6 hours after: when each ran, which agent, and which one started it. The way to find what set something up or carried it on.",
        parameters: Type.Object({ session: Type.String({ description: "S1, S2... or a session id" }) }),
        async execute(_id, args) {
            const s = sessionOf(args.session);
            if (!s) return unknown(args.session);
            const endpoint = await endpointOf(s);
            if (!endpoint) return text(`${s.ref} has no machine recorded, so its neighbours cannot be found.`);
            const at = (s.findings || []).map((f) => parseServerTimestamp(f.completedAt)).filter(Number.isFinite);
            const span = (await factsOf(s))?.window;
            const mid = at.length ? Math.min(...at) : Number.isFinite(span?.fromMs) ? span.fromMs : toMs;
            let rows;
            try {
                rows = await query(nearbySpec({ endpoint, fromMs: mid - 6 * 3600000, toMs: Math.min(nowMs, mid + 6 * 3600000) }));
            } catch (error) {
                return toolError(`Could not list ${s.ref}'s machine's sessions: ${error?.serverMessage || error?.message || error}`);
            }
            const seen = new Set();
            const lines = [];
            for (const r of rows || []) {
                const id = lower(r.trace_id);
                if (!id || seen.has(id)) continue;
                seen.add(id);
                const mark = id === s.id ? " (this session)" : list.some((x) => x.id === id) ? ` (in ${what})` : "";
                const parent = lower(r.parent_trace_id);
                lines.push(`  ${id}${mark}: ${timeOf(r.first_at)} to ${timeOf(r.last_at)}, ${r.provider || "?"}${r.surface ? ` ${r.surface}` : ""}${parent && parent !== id ? `, started by ${parent}` : ""}`);
            }
            const body = lines.length ? [`${s.ref}'s machine ran ${lines.length} session(s) around then:`, ...lines].join("\n") : `${s.ref}'s machine shows no other session around then.`;
            reads += 1;
            corpus.add(body);
            return text(body);
        },
    };

    // A session in outline (lib/sessionOutline.js), for a pass to start from:
    // read from its record before the pass, the messages it names counted as
    // shown. Empty when the record cannot be read; the tools say why.
    const outline = async (ref, { chapters = [] } = {}) => {
        const s = sessionOf(ref);
        if (!s) return "";
        let r;
        try { r = await loadRecord(s); } catch { return ""; }
        const got = sessionOutline(r.turns, { total: r.total, chapters });
        if (!got.text) return "";
        corpus.add(got.text);
        corpus.addMessages(s.id, got.named.map((t) => t?.messageId));
        return got.text;
    };

    // The files two or more of the sessions touched, from their records as
    // read: each by its folder and name, with where each session first
    // touched it (its turn as the outline numbers it, and that message's id,
    // counted as shown) and whether it changed it; the ones changed, and by
    // more of them, first. Sessions whose records cannot be read are left
    // out. [{ path, sessions: [{ id, ref, turn, messageId, changed }] }].
    const sharedFiles = async ({ max = SHARED_FILES_MAX } = {}) => {
        if (list.length < 2) return [];
        const files = new Map();
        for (const s of list) {
            let r;
            try { r = await loadRecord(s); } catch { continue; }
            const turns = Array.isArray(r?.turns) ? r.turns : [];
            let model;
            try { model = buildTraceLanes(turns); } catch { continue; }
            for (const lane of model?.lanes || []) {
                const key = lane.domain === "files" ? fileKey(lane.path) : "";
                const its = key ? (lane.items || []).filter((it) => Number.isInteger(it.turnIndex) && turns[it.turnIndex]?.role !== "gap") : [];
                if (!its.length) continue;
                if (!files.has(key)) files.set(key, { path: str(lane.path).replace(/\\/g, "/").split("/").filter(Boolean).slice(-2).join("/"), by: new Map() });
                const by = files.get(key).by;
                const first = Math.min(...its.map((it) => it.turnIndex));
                const changed = its.some((it) => markKindOf(it) === "write");
                const cur = by.get(s.id);
                if (!cur || first < cur.turn) by.set(s.id, { id: s.id, ref: s.ref, turn: first, messageId: turns[first]?.messageId || null, changed: changed || !!cur?.changed });
                else if (changed) cur.changed = true;
            }
        }
        const changedIn = (f) => [...f.by.values()].filter((b) => b.changed).length;
        const out = [...files.values()].filter((f) => f.by.size > 1)
            .sort((a, b) => changedIn(b) - changedIn(a) || b.by.size - a.by.size || a.path.localeCompare(b.path))
            .slice(0, max)
            .map((f) => ({ path: f.path, sessions: [...f.by.values()].sort((a, b) => list.indexOf(sessionOf(a.ref)) - list.indexOf(sessionOf(b.ref))) }));
        for (const f of out) for (const b of f.sessions) if (b.messageId) corpus.addMessages(b.id, [b.messageId]);
        return out;
    };

    return {
        tools: [readTurns, listCalls, readCall, search, machineRecords, nearby],
        corpus,
        sessions: list,
        outline,
        sharedFiles,
        stats: () => ({ reads }),
    };
}
